/**
 * AttachFilesButton — the composer's paperclip: opens the system file picker
 * (several files, the server's allowed types only) and hands the picked files
 * on. Off, titled with why, until those types are known.
 */
import { useRef } from 'react';

import { Paperclip } from '../../../design-system/icons';
import { Button } from '../../ui/button';
import {
  ATTACH_FILES_LABEL,
  ATTACH_FILES_LOADING_TITLE,
  ATTACH_FILES_UNAVAILABLE_TITLE,
  isFilePickerReady,
  type FilePicker,
} from './moments';

const ATTACH_INPUT_TESTID = 'attach-files-input';
const ATTACH_ICON_PX = 18;
/** Same height as the Send button beside the input. */
const ATTACH_BUTTON_CLASS = 'size-10 shrink-0 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed';
/** display:none keeps the native input out of the tab order and the a11y tree; the Paperclip button opens it. */
const HIDDEN_FILE_INPUT_CLASS = 'hidden';
const CLEARED_FILE_INPUT_VALUE = '';

export interface AttachFilesButtonProps {
  picker: FilePicker;
  disabled: boolean;
}

/** The button's title: why it's off while the allowed types aren't known, else its label. */
function titleFor(picker: FilePicker): string {
  if (picker.status === 'loading') return ATTACH_FILES_LOADING_TITLE;
  if (picker.status === 'unavailable') return ATTACH_FILES_UNAVAILABLE_TITLE;
  return ATTACH_FILES_LABEL;
}

/** The picker's `accept` list — none until the allowed types are known (the input is off meanwhile). */
function acceptFor(picker: FilePicker): string | undefined {
  if (!isFilePickerReady(picker)) return undefined;
  return picker.accept;
}

/** Paperclip over a hidden multi-file input limited to the allowed types. */
export function AttachFilesButton({ picker, disabled }: AttachFilesButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const ready = isFilePickerReady(picker);
  const openPicker = (): void => {
    inputRef.current?.click();
  };
  const handlePicked = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    // Copied before the reset below, which empties the input's own list.
    const picked = Array.from(ev.currentTarget.files ?? []);
    // Cleared after every pick so choosing the same file again still fires `change`.
    if (inputRef.current) inputRef.current.value = CLEARED_FILE_INPUT_VALUE;
    if (picked.length === 0) return;
    // The input is disabled until the picker is ready, so a pick always has somewhere to go.
    if (!isFilePickerReady(picker)) return;
    picker.onPick(picked);
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={acceptFor(picker)}
        disabled={!ready}
        onChange={handlePicked}
        className={HIDDEN_FILE_INPUT_CLASS}
        data-testid={ATTACH_INPUT_TESTID}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={openPicker}
        disabled={disabled || !ready}
        aria-label={ATTACH_FILES_LABEL}
        title={titleFor(picker)}
        className={ATTACH_BUTTON_CLASS}
      >
        <Paperclip size={ATTACH_ICON_PX} />
      </Button>
    </>
  );
}
